import { useEffect, useState, type ImgHTMLAttributes } from 'react';
import styles from './SafeImage.module.css';

export const PLACEHOLDER_IMAGEM_SRC = '/placeholders/imagem-indisponivel.svg';
export const PLACEHOLDER_AVATAR_SRC = '/placeholders/imagem-indisponivel-avatar.svg';

export type SafeImageVariant = 'avatar' | 'thumb' | 'logo' | 'media' | 'fill';

export type SafeImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src?: string | null;
  variant?: SafeImageVariant;
};

function placeholderPara(variant: SafeImageVariant): string {
  return variant === 'avatar' ? PLACEHOLDER_AVATAR_SRC : PLACEHOLDER_IMAGEM_SRC;
}

function resolverSrc(src: string | null | undefined, variant: SafeImageVariant): string {
  const trimmed = src?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : placeholderPara(variant);
}

function ehPlaceholder(url: string): boolean {
  return url === PLACEHOLDER_IMAGEM_SRC || url === PLACEHOLDER_AVATAR_SRC;
}

export function SafeImage({
  src,
  alt = '',
  variant = 'fill',
  className,
  onError,
  ...rest
}: SafeImageProps) {
  const inicial = resolverSrc(src, variant);
  const [atual, setAtual] = useState(inicial);
  const [falhou, setFalhou] = useState(ehPlaceholder(inicial));

  useEffect(() => {
    const next = resolverSrc(src, variant);
    setAtual(next);
    setFalhou(ehPlaceholder(next));
  }, [src, variant]);

  const classes = [styles.root, styles[variant], className].filter(Boolean).join(' ');
  const placeholderSrc = placeholderPara(variant);

  return (
    <img
      {...rest}
      src={atual}
      alt={alt}
      className={classes}
      data-placeholder={falhou ? 'true' : undefined}
      onError={(e) => {
        onError?.(e);
        if (falhou || ehPlaceholder(atual)) return;
        setFalhou(true);
        setAtual(placeholderSrc);
      }}
    />
  );
}
